import React, {
  Children,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import cn from 'clsx';
import { throttle } from 'lodash';

import Slide from './components/slide/slide';
import {
  AngleLeftLightIcon,
  AngleRightLightIcon
} from '../../shared/icons/icons';

import { useInterval } from '../../shared/hooks/hooks';

import { Maybe } from '../../shared/types/graphql-types';

import styles from './carousel.scss';

interface CarouselProps {
  autoSlideInterval?: Maybe<number>;
  className?: string;
  hasFullWidthSlides?: boolean | string | number;
  isDraggable?: boolean;
  onDrag?: React.MouseEventHandler<HTMLDivElement>;
  onDragEnd?: React.MouseEventHandler<HTMLDivElement>;
  onDragStart?: React.MouseEventHandler<HTMLDivElement> | null;
  onResize?: () => void;
  onSlideChange?: (value: React.SetStateAction<number>) => void;
  showIndicators?: boolean;
  showNextButton?: boolean;
  showPrevButton?: boolean;
  slideIndex?: number;
}

function Carousel(props: React.PropsWithChildren<CarouselProps>) {
  const {
    autoSlideInterval,
    children,
    className,
    hasFullWidthSlides = false,
    isDraggable = false,
    onDrag,
    onDragEnd,
    onDragStart,
    onResize,
    onSlideChange,
    showIndicators,
    showNextButton = true,
    showPrevButton = true,
    slideIndex
  } = props;

  const [internalSlideIndex, setInternalSlideIndex] = useState(0);
  const [sliderLeftMargin, setSliderLeftMargin] = useState(0);
  const [hasStartedDragging, setHasStartedDragging] = useState(false);
  const [
    hasTotalSlideWidthGreaterThanContainer,
    setHasTotalSlideWidthGreaterThanContainer
  ] = useState(false);
  const [lastLeftScroll, setLastLeftScroll] = useState(0);
  const slideCount = Children.toArray(children).length;
  const [offScreenSlideCount, setOffScreenSlideCount] = useState(0);

  const slideListWrapperRef = useRef<HTMLDivElement>(null);
  const slideListRef = useRef<HTMLDivElement>(null);
  const currentSlideRef = useRef<HTMLDivElement | null>(null);
  const startXRef = useRef<HTMLDivElement | null>(null);

  const currentSlideIndex = Number.isInteger(slideIndex)
    ? Number(slideIndex)
    : internalSlideIndex;

  const shouldShowButtons =
    (isDraggable && hasTotalSlideWidthGreaterThanContainer) ||
    (!isDraggable && offScreenSlideCount > 0);

  const calcSlider = useCallback(() => {
    if (slideCount > 1 && slideListWrapperRef.current && slideListRef.current) {
      const slideListWrapper = slideListWrapperRef.current;
      const slideContainer = slideListRef.current;
      const slideElems = slideContainer.children;

      if (isDraggable) {
        setHasTotalSlideWidthGreaterThanContainer(
          slideContainer?.scrollWidth > slideContainer?.offsetWidth
        );
      } else if (hasFullWidthSlides) {
        setOffScreenSlideCount(slideCount - 1);
      } else {
        const slideWrapperWidth = slideListWrapper.offsetWidth;
        let numOfSlidesInView = 0;

        for (let s = 0; s < slideElems.length; s++) {
          const slideElem: HTMLElement = slideElems[s] as HTMLElement;

          if (slideElem) {
            const slideRightPos = slideElem.offsetLeft + slideElem.offsetWidth;
            const slideLeftPos = slideElem.offsetLeft;

            if (slideLeftPos >= 0 && slideRightPos <= slideWrapperWidth) {
              numOfSlidesInView++;
            } else if (numOfSlidesInView > 0) {
              break;
            }
          }
        }

        setOffScreenSlideCount(slideCount - numOfSlidesInView);
      }
    }
  }, [slideCount, hasFullWidthSlides, isDraggable]);

  function scrollTo(scrollLeftAmount) {
    const sliderList = slideListRef.current;
    const waitForAfterAnimDuration = 500;

    if (sliderList?.scrollTo) {
      sliderList.scrollTo({
        behavior: 'smooth',
        left: scrollLeftAmount
      });
    } else if (sliderList) {
      sliderList.scrollLeft = scrollLeftAmount;
    }

    setTimeout(() => {
      setLastLeftScroll(scrollLeftAmount);
    }, waitForAfterAnimDuration);
  }

  function handleSlideIndex(index) {
    if (index > -1 && index <= offScreenSlideCount) {
      if (onSlideChange) {
        onSlideChange(index);
      } else {
        setInternalSlideIndex(index);
      }
    }
  }

  function prevSlide() {
    if (isDraggable) {
      const sliderList = slideListRef.current;
      if (sliderList) {
        const scrollLeftAmount = sliderList.scrollLeft - sliderList.offsetWidth;
        scrollTo(scrollLeftAmount);
      }
    } else {
      if (currentSlideIndex > 0) {
        handleSlideIndex(currentSlideIndex - 1);
      }
    }
  }

  function nextSlide() {
    if (isDraggable) {
      const sliderList = slideListRef.current;
      if (sliderList) {
        const scrollLeftAmount = sliderList.scrollLeft + sliderList.offsetWidth;
        scrollTo(scrollLeftAmount);
      }
    } else {
      if (currentSlideIndex < offScreenSlideCount) {
        handleSlideIndex(currentSlideIndex + 1);
      }
    }
  }

  function goToSlide(slideIndex) {
    handleSlideIndex(slideIndex);
  }

  function swipeStart(e) {
    const [touch] = e.touches;
    startXRef.current = touch.clientX;
  }

  function swipeEnd(e) {
    const [touch] = e.changedTouches;
    const touchStartX = startXRef.current;
    const touchEndX = touch?.clientX;
    const minSwipeDist = 50;

    if (Math.abs(touchEndX - Number(touchStartX)) > minSwipeDist) {
      if (Number(touchStartX) > touchEndX) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  }

  const findCurrentSlideOnDrag = throttle(() => {
    const slideList = slideListRef.current;
    if (slideList?.children) {
      for (let c = 0; c < slideList.children.length; c++) {
        const parentPos = slideList.getBoundingClientRect();
        const slidePos = slideList.children[c].getBoundingClientRect();

        const relPos = slidePos.left - parentPos.left;

        if (relPos >= 0 && relPos < parentPos.width / 2) {
          handleSlideIndex(c);
        }
      }
    }
  }, 500);

  function dragStart(e) {
    if (onDragStart) {
      onDragStart(e);
    }

    startXRef.current = e?.pageX || 0;

    setHasStartedDragging(true);
  }

  function dragContinue(e) {
    if (!hasStartedDragging) {
      return;
    }

    if (onDrag) {
      onDrag(e);
    }

    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const dragDistance = (e?.pageX || 0) - Number(startXRef?.current);

    if (slideListRef.current) {
      slideListRef.current.scrollLeft = lastLeftScroll - dragDistance;
    }
  }

  function dragEnd(e) {
    if (onDragEnd) {
      onDragEnd(e);
    }

    setHasStartedDragging(false);
    if (slideListRef.current) {
      setLastLeftScroll(slideListRef.current.scrollLeft);
    }
  }

  function getIsPrevButtonDisabled() {
    return isDraggable
      ? slideListRef.current?.scrollLeft === 0
      : currentSlideIndex === 0;
  }

  function getIsNextButtonDisabled() {
    const slideList = slideListRef.current;

    return isDraggable && slideList
      ? slideList.scrollWidth === slideList.scrollLeft + slideList.offsetWidth
      : currentSlideIndex === offScreenSlideCount;
  }

  useLayoutEffect(() => {
    calcSlider();
  }, [slideCount, calcSlider]);

  useLayoutEffect(() => {
    function measureCarousel() {
      if (onResize) {
        onResize();
      }

      calcSlider();
    }

    if (!hasFullWidthSlides) {
      window.addEventListener('resize', measureCarousel);
    }

    return () => {
      window.removeEventListener('resize', measureCarousel);
    };
  }, [onResize, hasFullWidthSlides, calcSlider]);

  useLayoutEffect(() => {
    const slideElem = currentSlideRef.current;
    const slideListWrapper = slideListWrapperRef.current;
    const slideList = slideListRef.current;

    if (slideElem && !isDraggable) {
      const parentPos = (
        slideElem?.parentNode as HTMLDivElement
      )?.getBoundingClientRect();
      const slidePos = slideElem.getBoundingClientRect();

      let newSliderLeftMargin = parentPos.left - slidePos.left;

      if (
        currentSlideIndex > 0 &&
        slideListWrapper &&
        slideList &&
        slideListWrapper.offsetWidth - slideList.offsetWidth >
          newSliderLeftMargin
      ) {
        newSliderLeftMargin =
          slideListWrapper.offsetWidth - slideList.offsetWidth;
      }

      setSliderLeftMargin(newSliderLeftMargin);
    }
  }, [currentSlideIndex, isDraggable]);

  useInterval(
    () => {
      let slideIndex = internalSlideIndex + 1;
      if (internalSlideIndex === (slideCount || 1) - 1) {
        slideIndex = 0;
      }

      handleSlideIndex(slideIndex);
    },
    autoSlideInterval,
    [internalSlideIndex]
  );

  return (
    <div
      className={cn(
        showIndicators ? styles['container-w-indicators'] : styles['container'],
        `${className || ''} carousel`
      )}
    >
      {shouldShowButtons && showPrevButton && (
        <button
          aria-label="Previous slide"
          className={cn(styles['prev'], 'carousel__prev-button')}
          data-testid="prev-button"
          disabled={getIsPrevButtonDisabled()}
          onClick={prevSlide}
          type="button"
        >
          <AngleLeftLightIcon />
        </button>
      )}
      <div
        className={cn(
          styles['slide-list-wrapper'],
          'carousel__slide-list-wrapper'
        )}
        ref={slideListWrapperRef}
      >
        <div
          className={cn(
            !isDraggable
              ? styles['slide-list']
              : styles['slide-list-draggable'],
            'carousel__slide-list'
          )}
          data-testid="carousel-slides"
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
            isDraggable ? dragStart(e) : null
          }
          onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) =>
            isDraggable ? dragEnd(e) : null
          }
          onMouseMove={(e: React.MouseEvent<HTMLDivElement>) =>
            isDraggable ? dragContinue(e) : null
          }
          onMouseUp={(e: React.MouseEvent<HTMLDivElement>) =>
            isDraggable ? dragEnd(e) : null
          }
          onScroll={findCurrentSlideOnDrag}
          onTouchEnd={swipeEnd}
          onTouchStart={swipeStart}
          ref={slideListRef}
          role="button"
          style={{
            marginLeft: hasFullWidthSlides
              ? `${currentSlideIndex * -100}%`
              : sliderLeftMargin,
            width: hasFullWidthSlides ? `${slideCount || 1}00%` : ''
          }}
          tabIndex={0}
        >
          {React.Children.map(children, (child, idx) => {
            if (
              (child as React.ReactElement)?.type['displayName'] === 'Slide'
            ) {
              return React.cloneElement(child as React.ReactElement, {
                ...(child as React.ReactElement).props,
                __INHERITED_PROPS__: {
                  isFullWidth: hasFullWidthSlides
                },
                ref: currentSlideIndex === idx ? currentSlideRef : null
              });
            } else if (child !== null) {
              console.error('children of Carousel must be a Slide components');
              return null;
            }
          })}
        </div>
      </div>
      {shouldShowButtons && showNextButton && (
        <button
          aria-label="Next slide"
          className={cn(styles['next'], 'carousel__next-button')}
          data-testid="next-button"
          disabled={getIsNextButtonDisabled()}
          onClick={nextSlide}
          type="button"
        >
          <AngleRightLightIcon />
        </button>
      )}
      {showIndicators && (
        <div className={cn(styles['indicators'], 'carousel__indicators')}>
          {Array.isArray(children) &&
            children
              .slice(0, offScreenSlideCount + 1)
              .map((s, idx) => (
                <button
                  aria-label={`Show slide ${idx + 1} of ${
                    offScreenSlideCount + 1
                  }`}
                  className={cn(
                    currentSlideIndex === idx
                      ? styles['indicator-active']
                      : styles['indicator'],
                    `carousel__indicator ${
                      currentSlideIndex === idx
                        ? 'carousel__indicator--active'
                        : ''
                    }`
                  )}
                  data-testid={`carousel-slide-indicator-${idx}`}
                  key={`ci-${idx}`}
                  onClick={() => goToSlide(idx)}
                />
              ))}
        </div>
      )}
    </div>
  );
}

export default Carousel;
export { Slide };
